import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Zap, LogIn } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import LanguageSwitcher from '@/components/LanguageSwitcher';

export default function LoginPage() {
  const { user, login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('login.title'));
      navigate('/');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('login.errors.invalid'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left: hero */}
      <div className="relative hidden lg:block">
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1643730341346-2f53ce6bd7d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxlbGVjdHJpYyUyMHN0cmVldCUyMHN3ZWVwZXJ8ZW58MHx8fHwxNzc3NzI4MjE1fDA&ixlib=rb-4.1.0&q=85')"
        }} />
        <div className="absolute inset-0 bg-[#0F172A]/80" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#002FA7] flex items-center justify-center">
              <Zap className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-bold text-xl">EV SERVICE ASSISTANT</div>
              <div className="overline text-gray-300">{t('login.subtitle')}</div>
            </div>
          </div>
          <div>
            <h1 className="font-display text-4xl xl:text-5xl font-black leading-tight tracking-tight">
              {t('login.subtitle')}
            </h1>
          </div>
          <div className="overline text-gray-400">© 2026 · EV Service Assistant</div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex flex-col justify-center px-6 sm:px-16 py-16 bg-white">
        <div className="flex justify-end mb-6">
          <LanguageSwitcher compact />
        </div>
        <div className="lg:hidden flex items-center gap-3 mb-10">
          <div className="w-9 h-9 bg-[#002FA7] flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-bold">EV SERVICE ASSISTANT</div>
            <div className="overline text-gray-500">{t('login.subtitle')}</div>
          </div>
        </div>
        <div className="max-w-md w-full">
          <div className="overline text-[#002FA7] mb-3">{t('login.title').toUpperCase()}</div>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">
            {t('login.title')}
          </h2>
          <p className="text-gray-500 mt-2">{t('login.subtitle')}</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="overline text-gray-600 mb-1.5 block">{t('login.email')}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-base"
                data-testid="login-email"
                placeholder="name@company.com"
              />
            </div>
            <div>
              <label className="overline text-gray-600 mb-1.5 block">{t('login.password')}</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-base"
                data-testid="login-password"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="login-submit"
            >
              <LogIn className="w-4 h-4" />
              {loading ? t('login.submitting') : t('login.submit')}
            </button>
          </form>

          <div className="mt-8 p-4 bg-gray-50 border border-gray-200 text-xs text-gray-600 font-mono">
            <div className="overline text-gray-500 mb-2">DEMO</div>
            <div>Admin: admin@evservice.it / Admin123!</div>
            <div>Tecnico: tecnico@evservice.it / Tecnico123!</div>
          </div>
        </div>
      </div>
    </div>
  );
}
